import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { owner, files as filesApi } from '../services/api';
import { toast } from '../hooks/use-toast';
import TierGate from '../components/TierGate';
import AppImage from '../components/AppImage';
import BusinessLogoAvatar from '../components/BusinessLogoAvatar';
import { PBB_CATEGORIES } from '../components/CategoryChips';
import { Plus, Edit3, Trash2, X, Camera, Loader2, Building2, MapPin, Ticket, Star, Video, Link2, Globe, Facebook, Instagram, Youtube, Twitter, Music2, Crown, Info, Calendar, Clock, Tag, AlertTriangle, Users } from 'lucide-react';

// Re-export the master list under the local name for downstream code.
const CATEGORIES = PBB_CATEGORIES;

const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'];

const BLANK = {
  name:'', category: CATEGORIES[0], city:'', state:'', zip:'', phone:'', email:'',
  coupon:'', points: 100, image:'',
  description:'', website:'', hours:'', address:'',
  social: { facebook:'', instagram:'', x:'', tiktok:'', youtube:'' },
  gallery: [], logo:'', video:'', videoUrl:'',
};
const MAX_GALLERY = 5;

// A "Tier 1" (Category Listing / basic) business is limited to a stripped-down
// registration form — no branding, no coupons, no loyalty card. Anything with
// a paid package (`standard` or `featured`) unlocks the full editor.
const isTier1 = (biz) => {
  const pkg = (biz?.package || biz?.tier || '').toString().toLowerCase();
  return pkg === '' || pkg === 'basic' || pkg === 'category listing';
};

export default function ManageListings() {
  return (
    <TierGate feature="manage_listings">
      <ManageListingsInner />
    </TierGate>
  );
}

function ManageListingsInner() {
  const [params] = useSearchParams();
  const isOnboarding = params.get('onboarding') === '1';
  const [view, setView] = useState('listings');   // listings | events
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const coverRef = useRef(null);
  const logoRef = useRef(null);
  const galleryRef = useRef(null);
  const videoRef = useRef(null);

  const navigate = useNavigate();
  const [tierInfo, setTierInfo] = useState(null);
  const [limit, setLimit] = useState(null);
  const load = () => owner.myBusinesses().then(setItems).finally(() => setLoading(false));
  useEffect(() => { load(); owner.myTier().then(setTierInfo).catch(() => {}); }, []);

  // For a brand-new listing we default to Tier 1 (basic) — the paid tiers are
  // unlocked from the Upgrade Plan page after the listing exists.
  const tier1 = useMemo(() => (editing === 'new' ? (tierInfo?.planPackage || tierInfo?.package || 'basic') === 'basic' : isTier1(editing)), [editing, tierInfo]);

  const openNew = async () => {
    let t = tierInfo;
    try { t = await owner.myTier(); setTierInfo(t); } catch {}
    if (t && !t.hasSubscription) {
      navigate('/plans?reason=listing');
      return;
    }
    if (t && !t.canAddListing) {
      setLimit({ code: 'listing_limit_reached', tier_label: t.label, current_tier: t.package, max_listings: t.maxListings,
        message: `Your ${t.label} plan allows up to ${t.maxListings} business${t.maxListings === 1 ? '' : 'es'}.${t.package === 'featured' ? " That's the highest plan limit." : ' Upgrade to add another.'}` });
      return;
    }
    setForm(BLANK); setEditing('new');
  };
  const wantsCreate = params.get('create') === '1';
  useEffect(() => {
    if (wantsCreate && !loading) { openNew(); window.history.replaceState(null, '', '/business/manage'); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantsCreate, loading]);
  const maxCats = tierInfo?.maxCategoriesPerListing || 1;
  const maxPhotos = tierInfo?.maxPhotos ?? null;
  const maxGallery = maxPhotos == null ? MAX_GALLERY : Math.max(0, maxPhotos - (form.image ? 1 : 0));
  const openEdit = (b) => {
    setForm({
      ...BLANK,
      ...b,
      social: { facebook:'', instagram:'', x:'', tiktok:'', youtube:'', ...(b.social || {}) },
      gallery: Array.isArray(b.gallery) ? b.gallery : [],
    });
    setEditing(b);
  };
  const close = () => setEditing(null);
  const setF = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const setSocial = (k, v) => setForm(p => ({ ...p, social: { ...p.social, [k]: v } }));

  const onCover = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploadingCover(true);
    try {
      const res = await filesApi.upload(f, 'business');
      setF('image', res.url);
      toast({ title: 'Cover photo uploaded' });
    } catch (err) {
      toast({ title: 'Upload failed', description: err?.response?.data?.detail || 'Try again' });
    } finally { setUploadingCover(false); e.target.value=''; }
  };

  const onLogo = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploadingLogo(true);
    try {
      const res = await filesApi.upload(f, 'business');
      setF('logo', res.url);
      toast({ title: 'Logo uploaded' });
    } catch (err) {
      toast({ title: 'Upload failed', description: err?.response?.data?.detail || 'Try again' });
    } finally { setUploadingLogo(false); e.target.value=''; }
  };

  const onGallery = async (e) => {
    const list = Array.from(e.target.files || []);
    if (!list.length) return;
    const remaining = maxGallery - (form.gallery?.length || 0);
    if (remaining <= 0) {
      e.target.value='';
      if (maxPhotos != null) { setLimit({ code: 'media_limit_reached', current_tier: tierInfo?.package, message: `Your ${tierInfo?.label} plan allows ${maxPhotos} business photos per listing (cover + gallery). Upgrade to Full Growth Campaign for unlimited photos.` }); return; }
      toast({ title: `Gallery limit is ${MAX_GALLERY} photos` }); return;
    }
    const toUpload = list.slice(0, remaining);
    setUploadingGallery(true);
    try {
      const uploaded = [];
      for (const f of toUpload) {
        const res = await filesApi.upload(f, 'business');
        uploaded.push(res.url);
      }
      setForm(p => ({ ...p, gallery: [...(p.gallery || []), ...uploaded] }));
      toast({ title: `Added ${uploaded.length} photo${uploaded.length > 1 ? 's' : ''}` });
    } catch (err) {
      toast({ title: 'Upload failed', description: err?.response?.data?.detail || 'Try again' });
    } finally { setUploadingGallery(false); e.target.value=''; }
  };

  const removeGalleryAt = (i) => setForm(p => ({ ...p, gallery: (p.gallery || []).filter((_, idx) => idx !== i) }));

  const onVideo = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (!f.type.startsWith('video/')) { toast({ title: 'Please choose a video file' }); return; }
    setUploadingVideo(true);
    try {
      const res = await filesApi.upload(f, 'video');
      setF('video', res.url);
      setF('videoUrl', '');
      toast({ title: 'Video uploaded' });
    } catch (err) {
      toast({ title: 'Video upload failed', description: err?.response?.data?.detail || 'Try again' });
    } finally { setUploadingVideo(false); e.target.value=''; }
  };

  const save = async () => {
    // Tier 1 requires the 8 core fields; higher tiers only require name/category/city.
    if (tier1) {
      const missing = [];
      if (!form.category) missing.push('Business category');
      if (!form.name)     missing.push('Company name');
      if (!form.address)  missing.push('Address');
      if (!form.city)     missing.push('City');
      if (!form.state)    missing.push('State');
      if (!form.zip)      missing.push('Zip code');
      if (!form.phone)    missing.push('Phone number');
      if (!form.email)    missing.push('Email');
      if (missing.length) {
        toast({ title: 'Fill required fields', description: missing.join(', ') });
        return;
      }
    } else if (!form.name || !form.category || !form.city) {
      toast({ title: 'Fill required fields' }); return;
    }
    setSaving(true);
    // Build the payload. For Tier 1 we ONLY send the 8 approved fields so the
    // upgrade-only fields (logo/coupon/loyalty/etc.) can never be smuggled in.
    let payload;
    if (tier1) {
      payload = {
        category: form.category,
        name: form.name.trim(),
        address: form.address.trim(),
        city: form.city.trim(),
        state: form.state.trim().toUpperCase(),
        zip: form.zip.trim(),
        phone: form.phone.trim(),
        email: form.email.trim().toLowerCase(),
        categories: form.categories || [],
      };
    } else {
      payload = { ...form };
      payload.social = Object.fromEntries(Object.entries(payload.social || {}).filter(([, v]) => v));
    }
    try {
      if (editing === 'new') await owner.createBusiness(payload);
      else await owner.updateBusiness(editing.id, payload);
      await load();
      close();
      toast({ title: editing === 'new' ? 'Listing created!' : 'Listing updated' });
    } catch (err) {
      // Backend can return `detail` as a string OR a dict (e.g. tier limit hit:
      // {message, code, current_listings, max_listings}). Unwrap both cleanly
      // so users get a real reason instead of "[object Object]".
      const raw = err?.response?.data?.detail;
      if (raw?.code === 'subscription_required') { navigate('/plans?reason=listing'); return; }
      if (['listing_limit_reached', 'category_limit_reached', 'media_limit_reached'].includes(raw?.code)) { setLimit(raw); return; }
      let description = 'Try again';
      if (typeof raw === 'string') description = raw;
      else if (raw && typeof raw === 'object') description = raw.message || JSON.stringify(raw);
      toast({ title: 'Save failed', description });
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this listing? This cannot be undone.')) return;
    await owner.deleteBusiness(id);
    setItems(prev => prev.filter(b => b.id !== id));
    toast({ title: 'Listing deleted' });
  };

  return (
    <div className="space-y-6">
      {isOnboarding && items.length === 0 && !loading && (
        <div
          data-testid="onboarding-welcome-banner"
          className="pbb-card p-5 border-l-4 border-l-green-500 bg-gradient-to-r from-green-500/[0.07] to-transparent"
        >
          <div className="text-xs uppercase tracking-widest text-green-400 font-semibold">Welcome to PBB — Business Owner</div>
          <div className="mt-1 text-lg font-bold">Let's get your business into the directory</div>
          <p className="text-sm text-gray-300 mt-1 max-w-2xl">
            One more step: create your first listing so consumers can find you,
            redeem coupons, and earn loyalty points at your business. It only takes
            a minute.
          </p>
        </div>
      )}

      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold">Manage {view === 'events' ? 'Events' : 'Listings'}</h1>
          <p className="text-gray-400 text-sm">
            {view === 'events'
              ? 'Publish events for your business. Events go live immediately and are reviewed by admins.'
              : 'Create and edit the businesses that appear in the PBB directory.'}
          </p>
        </div>
        {view === 'listings' && (
          <button data-testid="add-listing-btn" onClick={openNew} className="px-4 py-2.5 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm inline-flex items-center gap-2">
            <Plus size={16}/> Add Listing
          </button>
        )}
      </div>

      <div className="flex items-center gap-1 border-b border-[#1f1f1f] overflow-x-auto -mx-1 px-1">
        <TabButton active={view === 'listings'} onClick={() => setView('listings')} icon={Building2} label="Listings" testId="manage-tab-listings"/>
        <TabButton active={view === 'events'} onClick={() => setView('events')} icon={Calendar} label="My Events" testId="manage-tab-events"/>
      </div>

      {view === 'events' ? (
        <MyEventsSection businesses={items} />
      ) : loading ? (
        <div className="flex items-center gap-2 text-gray-400"><Loader2 className="animate-spin" size={16}/> Loading...</div>
      ) : items.length === 0 ? (
        <div className="pbb-card p-10 text-center">
          <Building2 className="mx-auto text-green-400" size={32}/>
          <div className="mt-3 font-semibold">No listings yet</div>
          <p className="text-sm text-gray-400 mt-1">Create your first listing to show up in the directory.</p>
          <button onClick={openNew} className="pbb-btn-green mt-5 max-w-xs mx-auto">Add your first business</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map(b => (
            <div key={b.id} className="pbb-card overflow-hidden flex flex-col h-full">
              <div className="relative">
                <AppImage src={b.image} alt={b.name} rounded="none" />
                <div className="absolute -bottom-6 left-4 z-10">
                  <BusinessLogoAvatar business={b} size="md" />
                </div>
              </div>
              <div className="p-4 pt-8 flex flex-col flex-1">
                <div className="flex items-center justify-between">
                  <div className="font-semibold">{b.name}</div>
                  <span className="px-2 py-0.5 rounded text-[10px] uppercase tracking-wider bg-green-600/15 text-green-400 border border-green-600/30">{b.package || b.tier || 'Basic'}</span>
                </div>
                <div className="text-xs text-gray-400 mt-1 flex items-center gap-1"><MapPin size={12}/>{[b.city, b.state].filter(Boolean).join(', ')} • {b.category}</div>
                {b.coupon && <div className="mt-2 inline-flex items-center gap-2 px-2 py-1 rounded bg-green-600/10 border border-green-600/30 text-green-400 text-xs"><Ticket size={12}/> {b.coupon}</div>}
                <div className="flex items-center justify-between mt-3 text-xs text-gray-400">
                  <span className="inline-flex items-center gap-1"><Star size={12}/> {b.rating?.toFixed?.(1) || '5.0'} ({b.reviews || 0})</span>
                  <span>{(b.gallery?.length || 0)} extra photos {b.video || b.videoUrl ? '• video' : ''}</span>
                </div>
                <div className="grid grid-cols-2 gap-2 mt-3">
                  <button data-testid={`edit-listing-${b.id}`} onClick={() => openEdit(b)} className="px-3 py-2 rounded-lg border border-[#2a2a2a] hover:border-green-600 text-xs inline-flex items-center justify-center gap-1"><Edit3 size={12}/> Edit</button>
                  <button data-testid={`delete-listing-${b.id}`} onClick={() => remove(b.id)} className="px-3 py-2 rounded-lg border border-[#2a2a2a] hover:border-red-500 hover:text-red-400 text-xs inline-flex items-center justify-center gap-1"><Trash2 size={12}/> Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {limit && <PlanLimitDialog limit={limit} onClose={() => setLimit(null)} />}
      {editing && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-end sm:items-center justify-center p-4" onClick={close}>
          <div className="pbb-card w-full max-w-2xl p-5 max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4 sticky top-0 bg-[#0a0a0a] pb-2">
              <div>
                <div className="font-semibold text-lg" data-testid="listing-modal-title">
                  {editing === 'new' ? 'Add Business — Category Listing (Tier 1)' : (tier1 ? 'Edit Business — Category Listing (Tier 1)' : 'Edit Business')}
                </div>
                {tier1 && (
                  <div className="text-[11px] text-gray-500 flex items-center gap-1 mt-0.5">
                    <Info size={11}/> Basic tier — only directory fields. Upgrade to add logos, coupons, and loyalty cards.
                  </div>
                )}
              </div>
              <button onClick={close} className="text-gray-400"><X size={18}/></button>
            </div>

            {tier1 ? (
              <Tier1Form form={form} setF={setF} maxCats={maxCats} />
            ) : (
              <FullEditorSections
                form={form} setF={setF} setSocial={setSocial}
                coverRef={coverRef} onCover={onCover} uploadingCover={uploadingCover}
                logoRef={logoRef} onLogo={onLogo} uploadingLogo={uploadingLogo}
                galleryRef={galleryRef} onGallery={onGallery} uploadingGallery={uploadingGallery}
                removeGalleryAt={removeGalleryAt}
                videoRef={videoRef} onVideo={onVideo} uploadingVideo={uploadingVideo}
                maxCats={maxCats} maxGallery={maxGallery} maxLinks={tierInfo?.maxLinks ?? null}
              />
            )}

            {tier1 && (
              <div className="mt-5 rounded-xl border border-yellow-500/30 bg-yellow-500/5 p-4 flex items-start gap-3" data-testid="tier1-upgrade-hint">
                <Crown size={18} className="text-yellow-300 shrink-0 mt-0.5"/>
                <div className="text-xs text-yellow-100/80 leading-relaxed">
                  <div className="text-yellow-300 font-semibold text-sm mb-1">Want a logo, coupons, or loyalty card?</div>
                  Upgrade to <b>Business Growth ($36 / 3 mo)</b> or <b>Full Growth ($120 / 3 mo)</b> from the Upgrade Plan page to unlock branding, promotions, and full marketing tools.
                </div>
              </div>
            )}

            <div className="flex gap-2 mt-5 sticky bottom-0 bg-[#0a0a0a] pt-3">
              <button onClick={close} className="flex-1 px-3 py-2.5 rounded-lg border border-[#2a2a2a] text-sm">Cancel</button>
              <button data-testid="save-listing-btn" onClick={save} disabled={saving} className="flex-1 pbb-btn-green disabled:opacity-70 inline-flex items-center justify-center gap-2">
                {saving && <Loader2 className="animate-spin" size={14}/>} {editing === 'new' ? 'Create' : 'Save changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Tier1Form({ form, setF, maxCats }) {
  return (
    <div className="space-y-3">
      <Field label="Business category *">
        <select data-testid="biz-category" className="pbb-input" value={form.category} onChange={e=>setF('category', e.target.value)}>
          {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </Field>
      <ExtraCategories form={form} setF={setF} maxCats={maxCats} />
      <Field label="Company name *">
        <input data-testid="biz-name" className="pbb-input" placeholder="e.g. Norman Bryant Services" value={form.name} onChange={e=>setF('name', e.target.value)}/>
      </Field>
      <Field label="Street address *">
        <input data-testid="biz-address" className="pbb-input" placeholder="123 Peachtree Street" value={form.address} onChange={e=>setF('address', e.target.value)}/>
      </Field>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <Field label="City *">
          <input data-testid="biz-city" className="pbb-input" placeholder="Atlanta" value={form.city} onChange={e=>setF('city', e.target.value)}/>
        </Field>
        <Field label="State *">
          <select data-testid="biz-state" className="pbb-input" value={form.state} onChange={e=>setF('state', e.target.value.toUpperCase())}>
            <option value="">—</option>
            {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Zip code *">
          <input data-testid="biz-zip" className="pbb-input" placeholder="30303" maxLength={10} value={form.zip} onChange={e=>setF('zip', e.target.value)}/>
        </Field>
      </div>
      <Field label="Phone number *">
        <input data-testid="biz-phone" className="pbb-input" placeholder="(404) 555-0123" value={form.phone} onChange={e=>setF('phone', e.target.value)}/>
      </Field>
      <Field label="Email *">
        <input data-testid="biz-email" type="email" className="pbb-input" placeholder="hello@yourbusiness.com" value={form.email} onChange={e=>setF('email', e.target.value)}/>
      </Field>
    </div>
  );
}

function FullEditorSections({ form, setF, setSocial, coverRef, onCover, uploadingCover, logoRef, onLogo, uploadingLogo, galleryRef, onGallery, uploadingGallery, removeGalleryAt, videoRef, onVideo, uploadingVideo, maxCats, maxGallery, maxLinks }) {
  const linkCount = (form.website?.trim() ? 1 : 0) + Object.values(form.social || {}).filter(v => (v || '').trim()).length;
  const linkFull = maxLinks != null && linkCount >= maxLinks;
  const lockLink = (v) => linkFull && !(v || '').trim();
  return (
    <>
      <Section title="Basics">
        <div className="flex flex-col sm:flex-row items-start gap-4">
          <div className="w-full sm:w-64 aspect-[16/9] rounded-lg overflow-hidden bg-[#0d1f14] flex items-center justify-center relative shrink-0">
            {form.image
              ? <img src={filesApi.urlFor(form.image)} alt="Business cover preview" className="absolute inset-0 w-full h-full object-cover object-center" />
              : <Building2 className="text-gray-600" size={28}/>}
            {uploadingCover && <div className="absolute inset-0 bg-black/60 flex items-center justify-center"><Loader2 size={16} className="animate-spin text-white"/></div>}
          </div>
          <div className="flex-1">
            <button type="button" onClick={() => coverRef.current?.click()} disabled={uploadingCover} data-testid="upload-cover-btn" className="px-3 py-2 rounded-lg border border-[#2a2a2a] hover:border-green-600 text-xs inline-flex items-center gap-1">
              <Camera size={12}/> {form.image ? 'Change cover photo' : 'Upload cover photo'}
            </button>
            <input ref={coverRef} type="file" accept="image/*" className="hidden" onChange={onCover}/>
            <p className="text-xs text-gray-500 mt-2">JPEG, PNG or WEBP up to 8 MB · shown at 16:9 across every card. This is the WIDE hero photo — not your logo.</p>

            <div className="mt-5 flex items-center gap-3">
              <div className="w-16 h-16 rounded-full overflow-hidden bg-white ring-2 ring-emerald-500/30 flex items-center justify-center relative shrink-0">
                {form.logo
                  ? <img src={filesApi.urlFor(form.logo)} alt="Logo preview" className="w-full h-full object-contain object-center p-1" />
                  : <Building2 className="text-emerald-700" size={20}/>}
                {uploadingLogo && <div className="absolute inset-0 bg-black/60 flex items-center justify-center"><Loader2 size={14} className="animate-spin text-white"/></div>}
              </div>
              <div>
                <button type="button" onClick={() => logoRef.current?.click()} disabled={uploadingLogo} data-testid="upload-logo-btn" className="px-3 py-2 rounded-lg border border-[#2a2a2a] hover:border-green-600 text-xs inline-flex items-center gap-1">
                  <Camera size={12}/> {form.logo ? 'Change logo' : 'Upload logo'}
                </button>
                <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={onLogo}/>
                <p className="text-xs text-gray-500 mt-1">Small circular badge overlaid on your card — full logo shows (contained, not cropped).</p>
              </div>
            </div>
          </div>
        </div>
        <input className="pbb-input" placeholder="Business name *" value={form.name} onChange={e=>setF('name', e.target.value)}/>
        <select className="pbb-input" value={form.category} onChange={e=>setF('category', e.target.value)}>
          {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <ExtraCategories form={form} setF={setF} maxCats={maxCats} />
        <input className="pbb-input" placeholder="City, State *" value={form.city} onChange={e=>setF('city', e.target.value)}/>
        <input className="pbb-input" placeholder="Street address" value={form.address} onChange={e=>setF('address', e.target.value)}/>
        <input className="pbb-input" placeholder="Phone" value={form.phone} onChange={e=>setF('phone', e.target.value)}/>
        <input className="pbb-input" placeholder="Business hours (e.g. Mon–Sat 10am–8pm)" value={form.hours} onChange={e=>setF('hours', e.target.value)}/>
        <textarea rows={3} className="pbb-input" placeholder="Business description" value={form.description} onChange={e=>setF('description', e.target.value)}/>
      </Section>

      <Section title="Web Presence" icon={Globe}>
        {maxLinks != null && (
          <p className={`text-[11px] ${linkFull ? 'text-yellow-300' : 'text-gray-300'}`} data-testid="links-limit-note">
            {linkCount}/{maxLinks} links used (website + social media combined).{linkFull && <> <Link to="/business/upgrade" className="underline">Upgrade</Link> for unlimited links.</>}
          </p>
        )}
        <input className="pbb-input disabled:opacity-40" placeholder="https://your-website.com" value={form.website} onChange={e=>setF('website', e.target.value)} disabled={lockLink(form.website)} data-testid="biz-website"/>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <SocialInput icon={Facebook} placeholder="Facebook URL or handle" value={form.social.facebook} onChange={v => setSocial('facebook', v)} disabled={lockLink(form.social.facebook)}/>
          <SocialInput icon={Instagram} placeholder="Instagram URL or @handle" value={form.social.instagram} onChange={v => setSocial('instagram', v)} disabled={lockLink(form.social.instagram)}/>
          <SocialInput icon={Twitter} placeholder="X (Twitter) URL or @handle" value={form.social.x} onChange={v => setSocial('x', v)} disabled={lockLink(form.social.x)}/>
          <SocialInput icon={Music2} placeholder="TikTok URL or @handle" value={form.social.tiktok} onChange={v => setSocial('tiktok', v)} disabled={lockLink(form.social.tiktok)}/>
          <SocialInput icon={Youtube} placeholder="YouTube URL or @channel" value={form.social.youtube} onChange={v => setSocial('youtube', v)} disabled={lockLink(form.social.youtube)}/>
        </div>
      </Section>

      <Section title={`Photo Gallery (${form.gallery?.length || 0}/${maxGallery})`}>
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
          {(form.gallery || []).map((g, i) => (
            <div key={i} className="relative aspect-square rounded-lg overflow-hidden">
              <img src={filesApi.urlFor(g)} alt={`gallery ${i+1}`} className="w-full h-full object-cover"/>
              <button onClick={() => removeGalleryAt(i)} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/70 text-white flex items-center justify-center hover:bg-red-600"><X size={12}/></button>
            </div>
          ))}
          {(form.gallery?.length || 0) < maxGallery && (
            <button onClick={() => galleryRef.current?.click()} disabled={uploadingGallery}
              className="aspect-square rounded-lg border-2 border-dashed border-[#2a2a2a] hover:border-green-600 flex flex-col items-center justify-center text-gray-400 hover:text-green-400 text-xs gap-1">
              {uploadingGallery ? <Loader2 className="animate-spin" size={16}/> : <Plus size={20}/>}
              <span>Add photos</span>
            </button>
          )}
        </div>
        <input ref={galleryRef} type="file" accept="image/*" multiple className="hidden" onChange={onGallery}/>
        <p className="text-xs text-gray-500">Up to {MAX_GALLERY} additional photos, 8 MB each.</p>
      </Section>

      <Section title="Video" icon={Video}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <button onClick={() => videoRef.current?.click()} disabled={uploadingVideo}
              className="w-full px-3 py-2.5 rounded-lg border border-[#2a2a2a] hover:border-green-600 text-sm inline-flex items-center justify-center gap-2">
              {uploadingVideo ? <Loader2 className="animate-spin" size={14}/> : <Camera size={14}/>}
              {form.video ? 'Replace uploaded video' : 'Upload video (max 50 MB)'}
            </button>
            <input ref={videoRef} type="file" accept="video/*" className="hidden" onChange={onVideo}/>
            {form.video && (
              <div className="mt-2 text-xs text-green-400 flex items-center gap-2">
                <Video size={12}/> Uploaded video attached
                <button onClick={() => setF('video','')} className="ml-auto text-gray-400 hover:text-red-400"><X size={12}/></button>
              </div>
            )}
          </div>
          <div>
            <div className="relative">
              <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={14}/>
              <input className="pbb-input pl-9" placeholder="Or paste YouTube / Vimeo URL" value={form.videoUrl} onChange={e => { setF('videoUrl', e.target.value); if (e.target.value) setF('video',''); }}/>
            </div>
            <p className="text-xs text-gray-500 mt-1">Uploading a video clears the link, and vice versa.</p>
          </div>
        </div>
      </Section>

      <Section title="Offer">
        <input className="pbb-input" placeholder="Short coupon summary (e.g. 20% OFF entrees)" value={form.coupon} onChange={e=>setF('coupon', e.target.value)}/>
        <input className="pbb-input" type="number" placeholder="Points per redemption" value={form.points} onChange={e=>setF('points', parseInt(e.target.value||'0', 10))}/>
        <p className="text-xs text-gray-500">You can add multiple full coupons from the coupon manager after saving.</p>
      </Section>
    </>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div className="mt-4 space-y-3">
      <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold flex items-center gap-2">
        {Icon && <Icon size={12} className="text-green-400"/>} {title}
      </div>
      {children}
    </div>
  );
}

function SocialInput({ icon: Icon, placeholder, value, onChange, disabled }) {
  return (
    <div className="relative">
      <Icon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"/>
      <input className="pbb-input pl-9 disabled:opacity-40" placeholder={placeholder} value={value || ''} onChange={e => onChange(e.target.value)} disabled={disabled}/>
    </div>
  );
}

function TabButton({ active, onClick, icon: Icon, label, testId }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId}
      className={`inline-flex items-center gap-2 px-4 py-2 text-sm border-b-2 transition-colors whitespace-nowrap
        ${active ? 'border-green-500 text-white font-semibold' : 'border-transparent text-gray-400 hover:text-white'}`}>
      <Icon size={14}/> {label}
    </button>
  );
}

/* ============================================================
   Owner-hosted events — auto-publish with admin review flag
   ============================================================ */
// Alias for the field wrapper defined above (Field). MyEventsSection uses <FormField>.
const FormField = Field;

const EVENT_BLANK = {
  title: '', date: '', time: '', city: '', location: '', address: '',
  price: 0, image: '', description: '', businessId: '', ticketUrl: '', capacity: 0,
};

function MyEventsSection({ businesses }) {
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null);   // null | 'new' | event object
  const [form, setForm] = useState(EVENT_BLANK);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const imgRef = useRef(null);

  const load = async () => {
    try { setRows(await owner.myEvents()); }
    catch { toast({ title: 'Failed to load events' }); setRows([]); }
  };
  useEffect(() => { load(); }, []);

  const openNew  = () => { setForm({ ...EVENT_BLANK, businessId: businesses?.[0]?.id || '' }); setEditing('new'); };
  const openEdit = (ev) => {
    setForm({
      title: ev.title || '', date: ev.date || '', time: ev.time || '',
      city: ev.city || '', location: ev.location || '', address: ev.address || '',
      price: ev.price ?? 0, image: ev.image || '', description: ev.description || '',
      businessId: ev.businessId || '', ticketUrl: ev.ticketUrl || '', capacity: ev.capacity ?? 0,
    });
    setEditing(ev);
  };
  const close = () => { setEditing(null); setForm(EVENT_BLANK); };
  const setF = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const r = await filesApi.upload(file, 'post');
      setF('image', r.url);
      toast({ title: 'Cover image uploaded' });
    } catch (err) {
      toast({ title: 'Upload failed', description: err?.response?.data?.detail || 'Try again' });
    } finally { setUploading(false); if (imgRef.current) imgRef.current.value = ''; }
  };

  const save = async () => {
    if (!form.title.trim()) { toast({ title: 'Event title is required' }); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        title: form.title.trim(),
        price: form.price === '' ? 0 : Number(form.price) || 0,
        capacity: form.capacity === '' ? 0 : Math.max(0, Number(form.capacity) || 0),
        businessId: form.businessId || null,
      };
      if (editing === 'new') await owner.createEvent(payload);
      else                    await owner.updateEvent(editing.id, payload);
      toast({ title: editing === 'new' ? 'Event published — awaiting admin review' : 'Event updated' });
      close();
      await load();
    } catch (err) {
      toast({ title: 'Save failed', description: err?.response?.data?.detail || 'Try again' });
    } finally { setSaving(false); }
  };

  const remove = async (ev) => {
    if (!window.confirm(`Delete "${ev.title}"? This cannot be undone.`)) return;
    try { await owner.deleteEvent(ev.id); toast({ title: 'Event deleted' }); await load(); }
    catch { toast({ title: 'Delete failed' }); }
  };

  return (
    <div className="space-y-4" data-testid="manage-events-section">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-xs text-gray-400 flex items-center gap-2">
          <Info size={12}/> Your events auto-publish. Admins review after the fact and can approve or remove them.
        </div>
        <button onClick={openNew} data-testid="add-event-btn"
          className="px-4 py-2.5 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm inline-flex items-center gap-2">
          <Plus size={16}/> Add Event
        </button>
      </div>

      {rows === null ? (
        <div className="flex items-center gap-2 text-gray-400"><Loader2 className="animate-spin" size={16}/> Loading events…</div>
      ) : rows.length === 0 ? (
        <div className="pbb-card p-10 text-center" data-testid="manage-events-empty">
          <Calendar className="mx-auto text-green-400" size={30}/>
          <div className="mt-3 font-semibold">No events yet</div>
          <p className="text-sm text-gray-400 mt-1">Publish your first event so PBB members can find and RSVP.</p>
          <button onClick={openNew} className="pbb-btn-green mt-5 max-w-xs mx-auto">Add your first event</button>
        </div>
      ) : (
        <ul className="space-y-3" data-testid="manage-events-list">
          {rows.map(ev => (
            <li key={ev.id} className="pbb-card p-3 flex items-start gap-3">
              <div className="w-20 h-20 rounded-lg overflow-hidden bg-black shrink-0">
                {ev.image
                  ? <img src={filesApi.urlFor(ev.image)} alt="" className="w-full h-full object-cover"/>
                  : <div className="w-full h-full flex items-center justify-center text-gray-500"><Calendar size={22}/></div>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-semibold text-white truncate">{ev.title}</div>
                  {ev.reviewStatus === 'approved' && <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-500/20 text-green-300 border border-green-500/40">APPROVED</span>}
                  {ev.reviewStatus === 'pending' && <span className="text-[10px] px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-300 border border-yellow-500/40">PENDING REVIEW</span>}
                </div>
                <div className="text-xs text-gray-400 flex items-center gap-3 mt-1 flex-wrap">
                  {ev.date && <span className="inline-flex items-center gap-1"><Calendar size={11}/> {ev.date}</span>}
                  {ev.time && <span className="inline-flex items-center gap-1"><Clock size={11}/> {ev.time}</span>}
                  {ev.city && <span className="inline-flex items-center gap-1"><MapPin size={11}/> {ev.city}</span>}
                  <span className="inline-flex items-center gap-1"><Tag size={11}/> {ev.price ? `$${ev.price}` : 'Free'}</span>
                  <span className="inline-flex items-center gap-1">👥 {ev.attending || 0} attending</span>
                </div>
                {ev.description && <div className="text-xs text-gray-500 mt-1 line-clamp-2">{ev.description}</div>}
              </div>
              <div className="flex flex-col gap-2 shrink-0">
                <Link to={`/events/${ev.id}/host`} data-testid={`host-attendees-${ev.id}`}
                  className="px-3 py-1.5 rounded-lg border border-green-500/40 text-green-300 hover:bg-green-500/10 text-xs inline-flex items-center gap-1 justify-center"><Users size={12}/> Attendees</Link>
                <button onClick={() => openEdit(ev)} data-testid={`edit-event-${ev.id}`}
                  className="px-3 py-1.5 rounded-lg border border-[#2a2a2a] hover:border-green-500 text-xs inline-flex items-center gap-1 justify-center"><Edit3 size={12}/> Edit</button>
                <button onClick={() => remove(ev)} data-testid={`delete-event-${ev.id}`}
                  className="px-3 py-1.5 rounded-lg border border-[#2a2a2a] hover:border-red-500 hover:text-red-400 text-xs inline-flex items-center gap-1 justify-center"><Trash2 size={12}/> Delete</button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-end sm:items-center justify-center p-4" onClick={close}>
          <div className="pbb-card w-full max-w-xl p-5 max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="font-semibold text-lg" data-testid="event-modal-title">
                {editing === 'new' ? 'Add Event' : 'Edit Event'}
              </div>
              <button onClick={close} className="p-1 rounded hover:bg-white/10 text-gray-400"><X size={18}/></button>
            </div>

            <div className="rounded-lg border border-yellow-500/40 bg-yellow-500/10 p-3 text-xs text-yellow-200 flex gap-2 mb-3">
              <AlertTriangle size={14} className="shrink-0 mt-0.5"/>
              <span>Your event will go live immediately. PBB admins review new events and may remove ones that violate our guidelines.</span>
            </div>

            <div className="space-y-3">
              <FormField label="Title *">
                <input className="pbb-input" value={form.title} onChange={e => setF('title', e.target.value)} data-testid="event-title-input"/>
              </FormField>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <FormField label="Date">
                  <input type="date" className="pbb-input" value={form.date} onChange={e => setF('date', e.target.value)} data-testid="event-date-input"/>
                </FormField>
                <FormField label="Time">
                  <input className="pbb-input" placeholder="e.g. 7:00 PM" value={form.time} onChange={e => setF('time', e.target.value)} data-testid="event-time-input"/>
                </FormField>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <FormField label="City">
                  <input className="pbb-input" value={form.city} onChange={e => setF('city', e.target.value)} data-testid="event-city-input"/>
                </FormField>
                <FormField label="Venue / Location">
                  <input className="pbb-input" placeholder="Venue name" value={form.location} onChange={e => setF('location', e.target.value)} data-testid="event-location-input"/>
                </FormField>
              </div>
              <FormField label="Address">
                <input className="pbb-input" value={form.address} onChange={e => setF('address', e.target.value)} data-testid="event-address-input"/>
              </FormField>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <FormField label="Price ($, 0 = free)">
                  <input type="number" min="0" step="0.01" className="pbb-input" value={form.price} onChange={e => setF('price', e.target.value)} data-testid="event-price-input"/>
                </FormField>
                <FormField label="Ticket / RSVP URL">
                  <input className="pbb-input" placeholder="https://" value={form.ticketUrl} onChange={e => setF('ticketUrl', e.target.value)} data-testid="event-ticket-input"/>
                </FormField>
              </div>
              <FormField label="Capacity (0 = unlimited)">
                <input type="number" min="0" step="1" className="pbb-input" value={form.capacity}
                  onChange={e => setF('capacity', e.target.value)}
                  data-testid="event-capacity-input"
                  placeholder="e.g. 100 (leave 0 for unlimited)"/>
              </FormField>
              {businesses?.length > 0 && (
                <FormField label="Attach to business (optional)">
                  <select className="pbb-input" value={form.businessId} onChange={e => setF('businessId', e.target.value)} data-testid="event-business-select">
                    <option value="">— None —</option>
                    {businesses.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                </FormField>
              )}
              <FormField label="Description">
                <textarea rows={3} className="pbb-input" value={form.description} onChange={e => setF('description', e.target.value)} data-testid="event-description-input"/>
              </FormField>
              <FormField label="Cover image">
                <div className="flex items-center gap-3">
                  <div className="w-24 h-24 rounded-lg overflow-hidden bg-black border border-[#2a2a2a] shrink-0">
                    {form.image
                      ? <img src={filesApi.urlFor(form.image)} alt="" className="w-full h-full object-cover"/>
                      : <div className="w-full h-full flex items-center justify-center text-gray-500"><Camera size={22}/></div>}
                  </div>
                  <div className="space-y-2">
                    <input ref={imgRef} type="file" hidden accept="image/*" onChange={e => upload(e.target.files?.[0])} data-testid="event-image-input"/>
                    <button type="button" onClick={() => imgRef.current?.click()} disabled={uploading}
                      className="px-3 py-1.5 rounded-lg border border-[#2a2a2a] hover:border-green-500 text-xs inline-flex items-center gap-1 disabled:opacity-60">
                      {uploading ? <Loader2 size={12} className="animate-spin"/> : <Camera size={12}/>}
                      {form.image ? 'Replace image' : 'Upload image'}
                    </button>
                    {form.image && (
                      <button type="button" onClick={() => setF('image', '')}
                        className="px-3 py-1.5 rounded-lg border border-red-500/50 text-red-400 hover:bg-red-500/10 text-xs inline-flex items-center gap-1 ml-2">
                        <Trash2 size={12}/> Remove
                      </button>
                    )}
                  </div>
                </div>
              </FormField>
            </div>

            <div className="flex justify-end gap-2 mt-5">
              <button onClick={close} className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white">Cancel</button>
              <button onClick={save} disabled={saving} data-testid="event-save-btn"
                className="px-4 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm inline-flex items-center gap-2 disabled:opacity-60">
                {saving ? <Loader2 size={14} className="animate-spin"/> : <Plus size={14}/>}
                {editing === 'new' ? 'Publish event' : 'Save changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ExtraCategories({ form, setF, maxCats }) {
  const extra = (form.categories || []).filter(c => c !== form.category);
  const slots = Math.max(0, (maxCats || 1) - 1);
  const toggle = (c) => setF('categories', extra.includes(c) ? extra.filter(x => x !== c) : [...extra, c]);
  if (slots === 0) {
    return (
      <p className="text-[11px] text-gray-300" data-testid="extra-categories-locked">
        Your plan includes 1 category listing. <Link to="/business/upgrade" className="text-yellow-300 underline">Upgrade</Link> to appear in more categories.
      </p>
    );
  }
  return (
    <div data-testid="extra-categories">
      <div className="text-xs text-gray-300 mb-1.5">Additional categories ({extra.length}/{slots}) · your plan allows {maxCats} category listings per business</div>
      <div className="flex flex-wrap gap-1.5">
        {CATEGORIES.filter(c => c !== form.category).map(c => {
          const on = extra.includes(c);
          return (
            <button key={c} type="button" disabled={!on && extra.length >= slots} onClick={() => toggle(c)} data-testid={`extra-cat-${c}`}
              className={`px-2.5 py-1 rounded-full text-[11px] border ${on ? 'bg-green-600 border-green-600 text-white' : 'border-[#2a2a2a] text-gray-200'} disabled:opacity-35`}>
              {c}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function PlanLimitDialog({ limit, onClose }) {
  const top = limit.current_tier === 'featured';
  const isCat = limit.code === 'category_limit_reached';
  const isMedia = limit.code === 'media_limit_reached';
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="plan-limit-dialog">
      <div className="w-full max-w-md rounded-2xl bg-[#0b0b0b] border border-yellow-500/40 p-6">
        <div className="flex items-center gap-2 text-yellow-300 font-semibold"><Crown size={18}/> {isMedia ? 'Plan limit reached' : isCat ? 'Category limit reached' : 'Business limit reached'}</div>
        <p className="mt-3 text-sm text-gray-100" data-testid="plan-limit-message">{limit.message}</p>
        <div className="mt-3 rounded-lg border border-[#2a2a2a] p-3 text-xs text-gray-200 space-y-1">
          <div>Tier 1 · Category Listing: 1 business, 1 category each</div>
          <div>Tier 2 · Business Growth: up to 2 businesses, 3 categories, 3 photos & 3 links each</div>
          <div>Tier 3 · Full Growth Campaign: up to 4 businesses, 4 categories each, unlimited photos & links, custom sign-up form + survey</div>
        </div>
        <div className="mt-5 flex gap-2">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 rounded-full border border-[#2a2a2a] text-sm text-gray-200" data-testid="plan-limit-close">Close</button>
          {!top && (
            <Link to="/business/upgrade" className="flex-1 text-center px-4 py-2.5 rounded-full bg-yellow-400 hover:bg-yellow-300 text-black text-sm font-semibold" data-testid="plan-limit-upgrade">
              Upgrade my plan
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
